import { useState, useEffect } from "react";
import FUpload from "../../commons/formFields/FUpload";
import imageCompression from "browser-image-compression";
import { Tooltip } from "antd";
import logger from "../../../utils/logger";

const ImageUploadButton = ({
  count = 1,
  aspectRatio,
  disabled,
  label,
  tooltip,
  required,
  helperText,
  images: imagesProp,
  onSave,
  onDelete,
  text,
}) => {
  const [images, setImages] = useState([]);
  const [imageList, setImageList] = useState(
    Array.from({ length: count }, () => [])
  );
  const [imageChanged, setImageChanged] = useState(false);

  // getDerivedStateFromProps equivalent: sync imageList from props until user edits
  useEffect(() => {
    if (imageChanged) return;
    if (imagesProp && imagesProp.length > 0) {
      const derived = imagesProp
        .map((imageItem, index) => {
          if (imageItem) {
            const url =
              typeof imageItem === "string"
                ? imageItem
                : imageItem.preview ||
                  (imageItem instanceof File
                    ? URL.createObjectURL(imageItem)
                    : null);
            if (url) return [{ uid: -1 * index - 1, url }];
          }
          return null;
        })
        .filter(Boolean);
      setImageList(derived);
    } else {
      setImageList(Array.from({ length: count }, () => []));
    }
  }, [imagesProp, imageChanged, count]);

  const uploadAction = (file, fileList, index) => {
    setImages((prev) => {
      const next = [...prev];
      next[index] = file;
      return next;
    });
  };

  const handleImageChange = async ({ fileList }, index) => {
    const nextImages = [...images];
    const nextImageList = [...imageList];

    if (fileList.length === 0) {
      nextImages[index] = "";
    } else {
      const file = fileList[0].originFileObj;
      logger.info("[ImageUploadButton] Received file for compression", {
        type: file?.type,
        size: file?.size,
        name: file?.name,
        isFile: file instanceof File,
        isBlob: file instanceof Blob,
      });

      try {
        const options = {
          maxSizeMB: 3,
          maxWidthOrHeight: 2560,
          useWebWorker: true,
          alwaysKeepResolution: true,
          preserveExif: true,
        };
        const compressedImage = await imageCompression(file, options);
        logger.info("[ImageUploadButton] Compression succeeded", {
          type: compressedImage?.type,
          size: compressedImage?.size,
          originalSize: file?.size,
        });
        if (compressedImage instanceof File || compressedImage instanceof Blob) {
          nextImages[index] = compressedImage;
        }
      } catch (error) {
        console.error("Error compressing image:", error);
        logger.warn("[ImageUploadButton] Compression failed, using original", {
          error: error?.message,
          fileType: file?.type,
          fileSize: file?.size,
          isFile: file instanceof File,
          isBlob: file instanceof Blob,
        });
        nextImages[index] = file;
      }
    }

    if (!imageChanged) {
      setImageChanged(true);
    }

    nextImageList[index] = fileList;
    setImages(nextImages);
    setImageList(nextImageList);
    onSave && onSave(nextImages);
  };

  return (
    <div>
      {label && (
        <div className="col-md-4">
          <label>
            {label}
            {required && <span style={{ color: "red" }}> * </span>}
            {tooltip && (
              <Tooltip
                placement="topLeft"
                title={<div style={{ width: 400 }}>{tooltip}</div>}
              >
                <span className="border-0 m-0 p-0 ml-2">
                  <i className="fa fa-question-circle-o" />
                </span>
              </Tooltip>
            )}
          </label>
        </div>
      )}
      <div className={!label ? "col d-flex" : "d-flex"}>
        <div
          className="upload-button"
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {Array.from({ length: count }, (_, index) => (
            <FUpload
              key={index}
              action={(file, fileList) => uploadAction(file, fileList, index)}
              fileList={imageList[index] || []}
              onChange={({ fileList }) => handleImageChange({ fileList }, index)}
              aspectRatio={aspectRatio}
              display="col"
              viewMode={false}
              onRemove={onDelete}
              disabled={disabled}
              text={text}
            />
          ))}
        </div>
      </div>
      {helperText && (
        <div
          className="flex-center"
          style={{ fontSize: "14px", fontWeight: "400", paddingLeft: "8px" }}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default ImageUploadButton;
